import { Fragment } from 'react';
import { useLocation, Link } from 'wouter';
import {
    CheckCircle2,
    ArrowRight,
    ShieldCheck,
    Clock,
    CreditCard,
    Building2,
    HelpCircle,
} from 'lucide-react';
import MarketingNav from '../components/marketing/MarketingNav';
import MarketingFooter from '../components/marketing/MarketingFooter';
import { PRICING_CONFIG, MARKETING_PLAN_LIST } from '../config/pricingConfig';
import { trackMarketingEvent } from '../lib/analytics';
import { usePageSEO } from '../utils/seo';

export interface CapabilityComparisonItem {
    feature: string;
    trial: boolean;
    starter: boolean;
    operations: boolean;
}

export interface CapabilityComparisonGroup {
    category: string;
    items: CapabilityComparisonItem[];
}

export const VERIFIED_CAPABILITY_GROUPS: CapabilityComparisonGroup[] = [
    {
        category: 'Core Facility & Schedule Management',
        items: [
            {
                feature: 'Unlimited dock doors and warehouse users',
                trial: true,
                starter: true,
                operations: true,
            },
            {
                feature: 'Live warehouse schedule and dock board (Today, 7 Days & Month views)',
                trial: true,
                starter: true,
                operations: true,
            },
            {
                feature: 'Purchase Order management and CSV PO import',
                trial: true,
                starter: true,
                operations: true,
            },
            {
                feature: 'Carrier contact directory',
                trial: true,
                starter: true,
                operations: true,
            },
        ],
    },
    {
        category: 'Carrier Booking, Approvals & Dock Execution',
        items: [
            {
                feature: 'Carrier booking requests and PO validation',
                trial: true,
                starter: true,
                operations: true,
            },
            {
                feature: 'Manual appointment approval, time revision, and decline management',
                trial: true,
                starter: true,
                operations: true,
            },
            {
                feature: 'Dock assignment, reassignment, check-in and checkout',
                trial: true,
                starter: true,
                operations: true,
            },
            {
                feature: 'Basic receiving and discrepancy recording',
                trial: true,
                starter: true,
                operations: true,
            },
            {
                feature: 'Appointment history and schedule CSV export',
                trial: true,
                starter: true,
                operations: true,
            },
        ],
    },
    {
        category: 'Operations Automation, Loads & Advanced Analytics',
        items: [
            {
                feature: 'Rule-based automatic appointment approval',
                trial: true,
                starter: false,
                operations: true,
            },
            {
                feature: 'Consolidated Loads and linked outbound Sales Orders',
                trial: true,
                starter: false,
                operations: true,
            },
            {
                feature: 'Excel .xlsx PO import',
                trial: true,
                starter: false,
                operations: true,
            },
            {
                feature: 'Advanced dwell and detention analytics',
                trial: true,
                starter: false,
                operations: true,
            },
            {
                feature: 'Carrier reliability scorecards',
                trial: true,
                starter: false,
                operations: true,
            },
            {
                feature: 'Advanced receiving exception analysis and shortage follow-up POs',
                trial: true,
                starter: false,
                operations: true,
            },
            {
                feature: 'Historical delay-risk analysis and rule-based operational assistance',
                trial: true,
                starter: false,
                operations: true,
            },
            {
                feature: 'Deterministic Copilot for operational schedule, dwell, and risk queries',
                trial: true,
                starter: false,
                operations: true,
            },
        ],
    },
];

const PricingPage = () => {
    const [, setLocation] = useLocation();

    usePageSEO({
        title: 'Pricing | BayWhiz Warehouse Dock Scheduling Software',
        description:
            'Transparent per-facility USD pricing for BayWhiz warehouse dock scheduling software. Plans start at $99 USD/month. Start with a 30-day full-access free trial—no credit card required.',
        canonicalPath: '/pricing',
    });

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col">
            <MarketingNav variant="light" />

            <main className="flex-1">
                {/* 1. HERO */}
                <section className="pt-16 pb-12 px-6 bg-white border-b border-slate-200">
                    <div className="max-w-4xl mx-auto text-center">
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold uppercase tracking-wider mb-4">
                            Transparent Per-Facility Pricing
                        </span>
                        <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight mb-4">
                            Start with a 30-day full-access trial. {PRICING_CONFIG.startingPriceIntro}
                        </h1>
                        <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed mb-8">
                            Evaluate BayWhiz on your actual dock schedule for 30 days with no credit card required at signup. Subscribe in USD per facility only when you are ready to continue.
                        </p>

                        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-600 font-medium">
                            <span className="inline-flex items-center gap-1.5">
                                <Clock size={16} className="text-teal-600" />
                                30-day full-access trial
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <CreditCard size={16} className="text-teal-600" />
                                No credit card required
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <Building2 size={16} className="text-teal-600" />
                                Priced in USD per warehouse facility
                            </span>
                        </div>
                    </div>
                </section>

                {/* 2. PLAN CARDS */}
                <section className="py-16 px-6">
                    <div className="max-w-5xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
                            {MARKETING_PLAN_LIST.map((plan) => {
                                const isRecommended = Boolean(plan.recommended);
                                return (
                                    <div
                                        key={plan.id}
                                        className={`rounded-2xl bg-white p-8 flex flex-col relative transition-shadow ${
                                            isRecommended
                                                ? 'border-2 border-teal-600 shadow-xl'
                                                : 'border border-slate-200 shadow-sm'
                                        }`}
                                    >
                                        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                                            <span
                                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                                                    isRecommended
                                                        ? 'bg-teal-100 text-teal-800'
                                                        : 'bg-slate-100 text-slate-800'
                                                }`}
                                            >
                                                {isRecommended ? 'Recommended Facility Plan' : 'Standard Facility Plan'}
                                            </span>
                                        </div>

                                        <h2 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h2>
                                        <p className="text-sm text-slate-600 leading-relaxed mb-6 min-h-[44px]">
                                            {plan.tagline}
                                        </p>

                                        <div className="mb-6 pb-6 border-b border-slate-200">
                                            <div className="flex items-baseline gap-1.5">
                                                <span className="text-4xl sm:text-5xl font-extrabold text-slate-900">
                                                    ${plan.priceMonthlyUsd}
                                                </span>
                                                <span className="text-base font-semibold text-slate-600">
                                                    {plan.currency} / {plan.interval}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 mt-1.5 font-medium">
                                                Billed monthly in {plan.currency} {plan.billingUnit} after your 30-day free trial
                                            </p>
                                        </div>

                                        <div className="mb-8 flex-1">
                                            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                                                Verified platform capabilities
                                            </div>
                                            <ul className="space-y-3">
                                                {plan.features.map((feature, idx) => (
                                                    <li key={idx} className="flex items-start gap-3 text-sm text-slate-700">
                                                        <CheckCircle2
                                                            size={18}
                                                            className="text-teal-600 shrink-0 mt-0.5"
                                                        />
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                trackMarketingEvent('start_trial_click', {
                                                    event_category: 'engagement',
                                                    event_label: `pricing_page_${plan.id}`,
                                                });
                                                setLocation('/request-trial');
                                            }}
                                            className={`w-full h-12 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer ${
                                                isRecommended
                                                    ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-md shadow-teal-600/20'
                                                    : 'bg-slate-900 hover:bg-slate-800 text-white'
                                            }`}
                                        >
                                            Request Free Trial
                                            <ArrowRight size={16} />
                                        </button>
                                        <p className="text-center text-xs text-slate-500 mt-2.5">
                                            No credit card required · No sales call required
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* 3. HOW THE 30-DAY FREE TRIAL WORKS & WHAT HAPPENS AT EXPIRY */}
                <section className="py-16 px-6 bg-white border-y border-slate-200">
                    <div className="max-w-5xl mx-auto">
                        <div className="max-w-3xl mb-10">
                            <span className="text-xs font-bold uppercase tracking-wider text-teal-700 block mb-2">
                                Straightforward Trial Policy
                            </span>
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                                What happens during and after your 30-day trial
                            </h2>
                            <p className="text-slate-600 leading-relaxed">
                                {PRICING_CONFIG.trialSummary.subheadline}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6">
                                <div className="w-9 h-9 rounded-lg bg-teal-100 text-teal-800 font-bold text-sm flex items-center justify-center mb-4">
                                    1
                                </div>
                                <h3 className="font-bold text-slate-900 mb-2">Day 1: Full Operations Access</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    Create your manager account and configure unlimited dock doors, warehouse users, operating hours, and facility timezone with full Operations feature access—no credit card required.
                                </p>
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6">
                                <div className="w-9 h-9 rounded-lg bg-teal-100 text-teal-800 font-bold text-sm flex items-center justify-center mb-4">
                                    2
                                </div>
                                <h3 className="font-bold text-slate-900 mb-2">Days 1–30: Run Real Appointments</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    Share your public booking link with carriers, validate POs, approve bookings, assign dock doors, and track check-ins and dwell across your shift.
                                </p>
                            </div>

                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-6">
                                <div className="w-9 h-9 rounded-lg bg-teal-100 text-teal-800 font-bold text-sm flex items-center justify-center mb-4">
                                    3
                                </div>
                                <h3 className="font-bold text-slate-900 mb-2">After Day 30: Subscribe Only If Ready</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">
                                    {PRICING_CONFIG.trialSummary.expirationExplanation}
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 rounded-xl border border-teal-200 bg-teal-50/60 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex items-start gap-3">
                                <ShieldCheck className="text-teal-700 shrink-0 mt-0.5" size={20} />
                                <p className="text-sm text-slate-700">
                                    <strong>Free for freight carriers:</strong> {PRICING_CONFIG.trialSummary.carrierAccessNote}
                                </p>
                            </div>
                            <Link
                                href="/faq"
                                className="text-xs font-semibold text-teal-700 hover:text-teal-800 underline shrink-0"
                            >
                                Read Trial &amp; Billing FAQ &rarr;
                            </Link>
                        </div>
                    </div>
                </section>

                {/* 4. VERIFIED PLATFORM CAPABILITIES TABLE */}
                <section className="py-16 px-6 bg-slate-50">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center max-w-2xl mx-auto mb-12">
                            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                                Plan &amp; Feature Comparison
                            </h2>
                            <p className="text-slate-600 text-sm sm:text-base">
                                Every new facility starts with a 30-day free trial featuring full Operations access, no credit card required, and no automatic charge at expiration. Choose Starter ($99 USD/mo) or Operations ($199 USD/mo) per facility when you are ready to subscribe.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
                            <table className="w-full text-left border-collapse min-w-[640px]">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50">
                                        <th className="py-4 px-5 sm:px-6 text-xs font-bold uppercase tracking-wider text-slate-500">
                                            Feature
                                        </th>
                                        <th className="py-4 px-4 sm:px-6 text-xs font-bold uppercase tracking-wider text-slate-700 text-center w-40 sm:w-44">
                                            <div>30-Day Free Trial</div>
                                            <div className="text-[11px] font-medium normal-case text-teal-700 mt-0.5">
                                                Full Operations Access
                                            </div>
                                        </th>
                                        <th className="py-4 px-4 sm:px-6 text-xs font-bold uppercase tracking-wider text-slate-800 text-center w-40 sm:w-44">
                                            <div>Starter $99</div>
                                            <div className="text-[11px] font-medium normal-case text-slate-500 mt-0.5">
                                                USD / mo per facility
                                            </div>
                                        </th>
                                        <th className="py-4 px-4 sm:px-6 text-xs font-bold uppercase tracking-wider text-teal-700 text-center w-40 sm:w-44 bg-teal-50/40">
                                            <div>Operations $199</div>
                                            <div className="text-[11px] font-medium normal-case text-teal-700 mt-0.5">
                                                USD / mo per facility
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 text-sm">
                                    {VERIFIED_CAPABILITY_GROUPS.map((group) => (
                                        <Fragment key={group.category}>
                                            <tr className="bg-slate-50/70">
                                                <td
                                                    colSpan={4}
                                                    className="py-2.5 px-5 sm:px-6 text-xs font-bold uppercase tracking-wider text-slate-600"
                                                >
                                                    {group.category}
                                                </td>
                                            </tr>
                                            {group.items.map((item) => (
                                                <tr key={item.feature} className="hover:bg-slate-50/50">
                                                    <td className="py-3.5 px-5 sm:px-6 text-slate-700 font-medium">
                                                        {item.feature}
                                                    </td>
                                                    <td className="py-3.5 px-4 sm:px-6 text-center">
                                                        {item.trial ? (
                                                            <span className="inline-flex items-center justify-center text-teal-600 font-bold">
                                                                ✓ Included
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center justify-center text-slate-400 font-medium">
                                                                — Not included
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-3.5 px-4 sm:px-6 text-center">
                                                        {item.starter ? (
                                                            <span className="inline-flex items-center justify-center text-teal-600 font-bold">
                                                                ✓ Included
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center justify-center text-slate-400 font-medium">
                                                                — Not included
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="py-3.5 px-4 sm:px-6 text-center bg-teal-50/20">
                                                        {item.operations ? (
                                                            <span className="inline-flex items-center justify-center text-teal-600 font-bold">
                                                                ✓ Included
                                                            </span>
                                                        ) : (
                                                            <span className="inline-flex items-center justify-center text-slate-400 font-medium">
                                                                — Not included
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="text-xs text-slate-500 mt-3 text-center">
                            Starter is $99 USD/month per facility and Operations is $199 USD/month per facility, billed monthly after your 30-day full-access trial. Contact {PRICING_CONFIG.supportEmail} to activate a paid subscription.
                        </p>
                    </div>
                </section>

                {/* 5. FINAL CTA */}
                <section className="py-16 px-6 bg-white border-t border-slate-200">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className="text-3xl font-bold text-slate-900 mb-4">
                            Try BayWhiz on your dock schedule for 30 days
                        </h2>
                        <p className="text-base sm:text-lg text-slate-600 mb-8">
                            Set up your warehouse facility, add your dock doors, and start accepting carrier appointment requests today.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                type="button"
                                onClick={() => setLocation('/request-trial')}
                                className="w-full sm:w-auto px-8 py-4 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base shadow-md shadow-teal-600/20 transition-all inline-flex items-center justify-center gap-2 cursor-pointer"
                            >
                                Request Trial Access
                                <ArrowRight size={18} />
                            </button>
                            <Link
                                href="/faq"
                                className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white hover:bg-slate-50 text-slate-700 font-semibold text-base border border-slate-300 transition-all inline-flex items-center justify-center gap-2"
                            >
                                <HelpCircle size={18} />
                                Read Trial &amp; Billing FAQ
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <MarketingFooter variant="light" />
        </div>
    );
};

export default PricingPage;
